import emailjs from '@emailjs/browser';
import { useCallback, useEffect, useState } from 'react';
import {
    EMAILJS_CONFIG,
    EMAIL_TEMPLATE,
    SECURITY_CONFIG,
} from '../config/emailjs';
import { useRateLimit } from './useRateLimit';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

interface UseContactFormReturn {
  formData: ContactFormData;
  isSubmitting: boolean;
  isSubmitted: boolean;
  error: string;
  isBlocked: boolean;
  remainingTime: number;
  attempts: number;
  maxAttempts: number;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
  clearError: () => void;
}

export const useContactForm = (): UseContactFormReturn => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [emailjsReady, setEmailjsReady] = useState(false);

  // Rate limiting para prevenir spam
  const rateLimit = useRateLimit({
    maxAttempts: SECURITY_CONFIG.MAX_ATTEMPTS_PER_HOUR,
    windowMs: SECURITY_CONFIG.RATE_LIMIT_WINDOW,
  });

  // Inicializar EmailJS
  useEffect(() => {
    try {
      if (EMAILJS_CONFIG.PUBLIC_KEY && EMAILJS_CONFIG.PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
        emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
        setEmailjsReady(true);
      } else {
        setEmailjsReady(false);
      }
    } catch (error) {
      setError('Erro na configuração do sistema de e-mail');
      setEmailjsReady(false);
    }
  }, []);

  // Validações de segurança
  const validateFormData = (data: ContactFormData): string | null => {
    // Validação de nome
    if (!data.name.trim() || data.name.length < 2 || data.name.length > 100) {
      return 'Nome deve ter entre 2 e 100 caracteres';
    }

    // Validação de e-mail
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return 'E-mail inválido';
    }

    // Validação de mensagem
    if (
      !data.message.trim() ||
      data.message.length < 10 ||
      data.message.length > 2000
    ) {
      return 'Mensagem deve ter entre 10 e 2000 caracteres';
    }

    // Validação de telefone (opcional)
    if (data.phone && data.phone.length > 20) {
      return 'Telefone muito longo';
    }

    // Validação de empresa (opcional)
    if (data.company && data.company.length > 100) {
      return 'Nome da empresa muito longo';
    }

    return null;
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      // Sanitização básica de entrada
      const sanitizedValue = value.replace(
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        ''
      );

      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue,
      }));
      if (error) setError('');
    },
    [error]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Verificar rate limiting
      if (!rateLimit.checkRateLimit()) {
        const minutes = Math.ceil(rateLimit.remainingTime / 60000);
        setError(`Muitas tentativas. Tente novamente em ${minutes} minutos.`);
        return;
      }

      // Validação dos dados
      const validationError = validateFormData(formData);
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsSubmitting(true);
      setError('');

      try {
        // Verificar se as credenciais do EmailJS estão configuradas
        const hasValidCredentials = 
          emailjsReady &&
          EMAILJS_CONFIG.SERVICE_ID &&
          EMAILJS_CONFIG.TEMPLATE_ID &&
          EMAILJS_CONFIG.PUBLIC_KEY &&
          EMAILJS_CONFIG.SERVICE_ID !== 'YOUR_SERVICE_ID' &&
          EMAILJS_CONFIG.TEMPLATE_ID !== 'YOUR_TEMPLATE_ID' &&
          EMAILJS_CONFIG.PUBLIC_KEY !== 'YOUR_PUBLIC_KEY';

        if (!hasValidCredentials) {
          // Fallback: simular envio para testes
          await new Promise(resolve => setTimeout(resolve, 2000));

          setIsSubmitting(false);
          setIsSubmitted(true);
          rateLimit.recordAttempt(); // Registrar tentativa
          return;
        }

        // Envio real via EmailJS
        const templateParams = {
          // Variáveis principais do usuário
          user_name: formData.name.trim(),
          user_email: formData.email.trim().toLowerCase(),
          user_phone: formData.phone.trim() || 'Não informado',
          user_company: formData.company.trim() || 'Não informado',
          user_message: formData.message.trim(),

          // Variáveis adicionais para o template
          date: new Date().toLocaleDateString('pt-BR'),
          time: new Date().toLocaleTimeString('pt-BR'),
          priority: 'Média',

          // Variáveis alternativas para compatibilidade
          to_name: 'Avoprime',
          to_email: EMAILJS_CONFIG.TEST_EMAIL,
          from_name: formData.name.trim(),
          from_email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || 'Não informado',
          company: formData.company.trim() || 'Não informado',
          message: formData.message.trim(),
          subject: EMAIL_TEMPLATE.subject,
        };

        await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          templateParams,
          EMAILJS_CONFIG.PUBLIC_KEY
        );

        setIsSubmitting(false);
        setIsSubmitted(true);
        rateLimit.recordAttempt(); // Registrar tentativa bem-sucedida
      } catch (error) {
        setError(
          'Erro ao enviar mensagem. Tente novamente ou entre em contato diretamente.'
        );
        setIsSubmitting(false);
        rateLimit.recordAttempt(); // Registrar tentativa falhada
      }
    },
    [formData, rateLimit, emailjsReady]
  );

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    });
    setIsSubmitted(false);
    setError('');
    rateLimit.resetRateLimit(); // Reset do rate limiting
  }, [rateLimit]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    formData,
    isSubmitting,
    isSubmitted,
    error,
    isBlocked: rateLimit.isBlocked,
    remainingTime: rateLimit.remainingTime,
    attempts: rateLimit.attempts,
    maxAttempts: rateLimit.maxAttempts,
    handleInputChange,
    handleSubmit,
    resetForm,
    clearError,
  };
};
